import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Lock, Database, Eye, UserCheck, Cookie, Mail } from "lucide-react";

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Home</span>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-100 mb-6">
            <Shield className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Privacy Policy</h1>
          <p className="text-slate-500">Last Updated: December 2024</p>
          <p className="text-sm text-slate-400 mt-2">Compliant with IT Act 2000, IT Rules 2011 & DPDP Act 2023</p>
        </div>

        {/* Policy Content */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-8 md:p-12 space-y-10">
            
            {/* 1. Introduction */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 text-sm font-bold">1</span>
                Introduction
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>
                  <strong>CLARIAS</strong> ("we", "our", "us") is committed to protecting the privacy and personal data of our users ("you", "your"). This Privacy Policy explains how we collect, use, store, and protect your information when you use our AI-powered UPSC preparation platform.
                </p>
                <p>
                  This policy applies to all users of the CLARIAS website, mobile applications, and related services. By using CLARIAS, you consent to the practices described in this Privacy Policy.
                </p>
                <p>
                  This Privacy Policy is compliant with:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Information Technology Act, 2000</li>
                  <li>Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011</li>
                  <li>Digital Personal Data Protection Act, 2023 (DPDP Act)</li>
                </ul>
              </div>
            </section>

            {/* 2. Data Collected */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 text-sm font-bold">2</span>
                Data We Collect
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>We collect the following categories of personal data:</p>
                
                <div className="space-y-4 mt-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="font-medium text-slate-800 mb-2">Account Information</p>
                    <ul className="list-disc pl-6 space-y-1 text-sm">
                      <li>Name</li>
                      <li>Email address</li>
                      <li>Phone number (optional)</li>
                      <li>Login credentials (securely hashed)</li>
                    </ul>
                  </div>
                  
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="font-medium text-slate-800 mb-2">Usage Data</p>
                    <ul className="list-disc pl-6 space-y-1 text-sm">
                      <li>Test performance and scores</li>
                      <li>Practice session data</li>
                      <li>Progress reports and analytics</li>
                      <li>Feature usage patterns</li>
                    </ul>
                  </div>
                  
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="font-medium text-slate-800 mb-2">Payment Data</p>
                    <ul className="list-disc pl-6 space-y-1 text-sm">
                      <li>Transaction details (processed via secure third-party payment gateways)</li>
                      <li>Subscription history</li>
                    </ul>
                    <p className="text-xs text-slate-500 mt-2">Note: We do NOT store complete payment card details. All payment processing is handled by PCI-DSS compliant payment gateways.</p>
                  </div>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mt-4">
                  <p className="text-emerald-800 font-medium flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    Our Commitment
                  </p>
                  <p className="text-emerald-700 mt-2">
                    CLARIAS does <strong>NOT</strong> sell, rent, or trade your personal data to third parties for marketing purposes.
                  </p>
                </div>
              </div>
            </section>

            {/* 3. Purpose of Data Use */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 text-sm font-bold">3</span>
                Purpose of Data Use
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>We use your personal data for the following purposes:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Account Management:</strong> To create, maintain, and secure your user account</li>
                  <li><strong>Service Delivery:</strong> To provide AI-powered educational features and functionalities</li>
                  <li><strong>Personalisation:</strong> To customise your learning experience based on your progress and preferences</li>
                  <li><strong>Progress Tracking:</strong> To generate performance analytics, behaviour reports, and recommendations</li>
                  <li><strong>Payment Processing:</strong> To process subscription payments and maintain billing records</li>
                  <li><strong>Customer Support:</strong> To respond to your queries, feedback, and support requests</li>
                  <li><strong>Platform Improvement:</strong> To analyse usage patterns and improve our services</li>
                  <li><strong>Legal Compliance:</strong> To comply with applicable laws and legal obligations</li>
                </ul>
              </div>
            </section>

            {/* 4. Data Storage & Security */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 text-sm font-bold">4</span>
                Data Storage & Security
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>We implement reasonable technical and organisational safeguards to protect your personal data, including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Encryption of data in transit and at rest</li>
                  <li>Secure password hashing algorithms</li>
                  <li>Regular security audits and vulnerability assessments</li>
                  <li>Access controls limiting data access to authorised personnel</li>
                  <li>Secure cloud infrastructure from trusted providers</li>
                </ul>
                <p className="mt-4">
                  While we take all reasonable measures to protect your data, no system is completely secure. We cannot guarantee absolute security but commit to promptly addressing any security incidents.
                </p>
              </div>
            </section>

            {/* 5. Data Sharing */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 text-sm font-bold">5</span>
                Data Sharing
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>We may share your personal data ONLY with:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Payment Gateways:</strong> Razorpay for secure transaction processing</li>
                  <li><strong>Cloud & Infrastructure Providers:</strong> For hosting and data storage services</li>
                  <li><strong>Analytics Providers:</strong> For platform performance and usage analytics (anonymised where possible)</li>
                  <li><strong>Legal Authorities:</strong> When required by law, court order, or government request</li>
                </ul>
                <p className="mt-4">
                  All third-party service providers are contractually bound to maintain the confidentiality and security of your data.
                </p>
              </div>
            </section>

            {/* 6. User Rights (DPDP Act) */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 text-sm font-bold">6</span>
                Your Rights (Under DPDP Act, 2023)
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>As a data principal under the Digital Personal Data Protection Act, 2023, you have the following rights:</p>
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="font-medium text-slate-800 flex items-center gap-2">
                      <Eye className="w-4 h-4 text-emerald-600" />
                      Right to Access
                    </p>
                    <p className="text-sm mt-2">Request a summary of your personal data held by us</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="font-medium text-slate-800 flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-emerald-600" />
                      Right to Correction
                    </p>
                    <p className="text-sm mt-2">Request correction of inaccurate or incomplete data</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="font-medium text-slate-800 flex items-center gap-2">
                      <Database className="w-4 h-4 text-emerald-600" />
                      Right to Erasure
                    </p>
                    <p className="text-sm mt-2">Request deletion of your personal data</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4">
                    <p className="font-medium text-slate-800 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-emerald-600" />
                      Right to Withdraw Consent
                    </p>
                    <p className="text-sm mt-2">Withdraw previously given consent at any time</p>
                  </div>
                </div>
                <p className="mt-4">
                  To exercise any of these rights, please contact us at <a href="mailto:support@clarias.in" className="text-emerald-600 font-medium hover:underline">support@clarias.in</a>. We will respond to your request within the timeframe required by applicable law.
                </p>
              </div>
            </section>

            {/* 7. Cookies & Analytics */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 text-sm font-bold">7</span>
                Cookies & Analytics
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                {/* EXACT TEXT AS REQUIRED BY LEGAL PROMPT */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <p className="text-emerald-800 font-medium">
                    "CLARIAS uses cookies and analytics tools to improve platform performance and user experience."
                  </p>
                </div>
                
                <p className="mt-4">CLARIAS uses cookies and similar technologies for limited purposes:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Session Management:</strong> To keep you logged in and maintain your session</li>
                  <li><strong>Performance Analytics:</strong> To understand platform usage and improve user experience</li>
                  <li><strong>Platform Improvement:</strong> To identify and fix technical issues</li>
                </ul>
                <p className="mt-4">
                  We do not use cookies for third-party advertising or tracking across websites. You can manage cookie preferences through your browser settings.
                </p>
              </div>
            </section>

            {/* 8. Children's Privacy */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 text-sm font-bold">8</span>
                Children's Privacy
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>
                  CLARIAS is intended for users aged <strong>18 years and above</strong>. We do not knowingly collect personal data from individuals under the age of 18.
                </p>
                <p>
                  If we become aware that we have inadvertently collected data from a minor, we will take immediate steps to delete such information. If you believe a minor has provided us with personal data, please contact us at <a href="mailto:support@clarias.in" className="text-emerald-600 font-medium hover:underline">support@clarias.in</a>.
                </p>
              </div>
            </section>

            {/* 9. Policy Updates */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 text-sm font-bold">9</span>
                Policy Updates
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or platform features.
                </p>
                <p>
                  When we make significant changes, we will notify you through the platform or via email. The "Last Updated" date at the top of this policy indicates when it was last revised.
                </p>
                <p>
                  Continued use of CLARIAS after any changes constitutes your acceptance of the updated Privacy Policy.
                </p>
              </div>
            </section>

            {/* 10. Contact & Grievance Officer */}
            <section>
              <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 text-sm font-bold">10</span>
                Contact Us & Grievance Officer
              </h2>
              <div className="text-slate-600 space-y-3 leading-relaxed">
                <p>For any questions, concerns, or requests regarding this Privacy Policy or your personal data, please contact us at:</p>
                
                {/* Grievance Officer Section - MANDATORY */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                  <p className="font-semibold text-emerald-800 mb-2">Customer Support & Grievance Contact</p>
                  <div className="flex items-center gap-3 mb-3">
                    <Mail className="w-5 h-5 text-emerald-600" />
                    <a href="mailto:support@clarias.in" className="text-emerald-600 font-medium hover:underline text-lg">
                      support@clarias.in
                    </a>
                  </div>
                  <p className="text-sm text-emerald-700">
                    This email address also serves as the <strong>Grievance Officer contact</strong> under applicable Indian laws, 
                    including the Information Technology Act, 2000 and Digital Personal Data Protection Act, 2023.
                  </p>
                  <p className="text-sm text-emerald-600 mt-2">
                    Typical response time: 24–48 business hours.
                  </p>
                </div>
              </div>
            </section>

          </div>
        </div>

        {/* Related Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link to="/terms-of-service" className="text-emerald-600 hover:underline text-sm">Terms of Service</Link>
          <span className="text-slate-300">|</span>
          <Link to="/refund-policy" className="text-emerald-600 hover:underline text-sm">Refund Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
